import { useEffect, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Checkbox,
    Button,
    FormControlLabel,
} from "@mui/material";
import { useNav } from "../../context/NavProvider";
import { useClient } from "../../context/ClientProvider";
import { useAccessibility } from "../../context/AccessibilityProvider";
import { useAuth } from "../../context/AuthProvider";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallMeDialog from "../pages/CallMeDialog";
import ScrollToTop from '../nav/ScrollToTop';
import { useUtils } from "../../context/UtilsProvider";
import { useNavigate } from "react-router-dom";

const Accessibility = () => {

    /*Provider stuff*/
    const { formState } = useUtils();
    const { useGetConfig } = useClient();
    const { clientConfig } = useGetConfig();
    const { postAccessibility, postNote } = useAccessibility();
    const { accessToken } = useAuth();
    const [consentIsChecked, setConsentIsChecked] = useState(false);
    const [middleCheckboxesEnabled, setMiddleCheckboxesEnabled] = useState(false);
    const [callbackCheckboxesEnabled, setCallbackCheckboxesEnabled] = useState(false);
    const [notApplicableChecked, setNotApplicableChecked] = useState(false);
    const [ukRelayChecked, setUkRelayChecked] = useState(false);
    const [translatorChecked, setTranslatorChecked] = useState(false);
    const [additionalNeeds, setAdditionalNeeds] = useState(false);
    const [mentalHealthChecked, setMentalHealthChecked] = useState(false);
    const [physicalNeedsChecked, setPhysicalNeedsChecked] = useState(false);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(localStorage.getItem("accessibilitySubmitted"));
    const { setShowFooter } = useNav();
    const [openCallback, setOpenCallback] = useState(false);

    const navigate = useNavigate();
    setShowFooter(false);

    useEffect(() => {
        setMiddleCheckboxesEnabled(consentIsChecked);
    }, [consentIsChecked]);

    useEffect(() => {
        if (!consentIsChecked) {
            setSaveButtonEnabled(false);
        }
    }, [consentIsChecked]);

    useEffect(() => {
        const hasMiddleSelection = notApplicableChecked || ukRelayChecked || translatorChecked;
        setSaveButtonEnabled(hasMiddleSelection);
    }, [notApplicableChecked, ukRelayChecked, translatorChecked]);

    useEffect(() => {
        const hasMiddleSelection = notApplicableChecked || ukRelayChecked || translatorChecked;
        setCallbackCheckboxesEnabled(consentIsChecked && hasMiddleSelection);
    }, [consentIsChecked, notApplicableChecked, ukRelayChecked, translatorChecked]);

    useEffect(() => {
        window.scrollTo(0,0)
        navigate("/accessibility")
    }, [isSubmitted]);

    const handleSave = async () => {
        if (!consentIsChecked || !saveButtonEnabled) {
            console.warn("Consent or options missing.");
            return;
        }

        try {
            let payload = {
                NotApplicable: notApplicableChecked,
                UKRelayService: ukRelayChecked,
                Translator: translatorChecked,
                AudioFiles: false,
                LargePrint: false,
                MiscOption1: false,
                MiscOption2: false,
                MiscOption3: false,
            };

            if (!accessToken) {
                console.warn("Access token missing.");
            }
            localStorage.setItem("accessibilitySubmitted", true)
            setIsSubmitted(true)

            await postAccessibility(payload);

            await postNote({ Note: "CUSTOMER ACCESSIBILITY OPTIONS HAVE BEEN UPDATED" })

            if (notApplicableChecked) {
                await postNote({ Note: "Customer has indicated that accessibility options are not applicable" })
            }

            if (ukRelayChecked) {
                await postNote({ Note: "Customer has indicated that they use UK Relay Service when communicating on the phone." })
            }

            if (translatorChecked) {
                await postNote({ Note: "Customer has indicated that they speak through a translator or appointed 3rd party so will have someone with them if they contact us." })
            }

            if (additionalNeeds) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have additional accessibility needs that they would like to discuss with us." })
            }

            if (mentalHealthChecked) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have Mental Health needs that impacts them financially that they would like to discuss." })
            }

            if (physicalNeedsChecked) {
                await postNote({ Note: "CUSTOMER ALERT: This customer has expressed that they have Physical Health needs that impacts them financially that they would like to discuss." })
            }

            console.log("Saved.");

        } catch (error) {
            console.error("Save error:", error);
        }
    };

    const handleReturnHome = () => {
        window.location.href = "/account";
    };

    const handleCloseCallback = () => {
        setOpenCallback(false);
        window.location.href = "/account";
    };

    const handleOpenCallback = () => {
        setOpenCallback(true)
    }

    return (
        clientConfig && (
            <>
                <Container sx={{ mt: 4, paddingBottom: "750px" }}>
                    <ScrollToTop />
                    {!isSubmitted ? (
                        <>
                            <Box>
                                <Typography fontSize={24} fontWeight={"bold"} paddingTop={1} paddingBottom={3}>
                                    Accessibility
                                </Typography>
                                <Typography type="body1">
                                    At {clientConfig?.name}, our customers are at the heart of everything that we do. To help ensure we can support appropriately and meet your needs there are a few questions we'd like to ask you.
                                </Typography>

                                <Typography type="body1" sx={{ fontWeight: "bold", mb: 1, mt: 3 }}>
                                    We will store this information alongside your other account details, so that we can provide you with the appropriate support you need.
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={consentIsChecked}
                                            onChange={() => {
                                                setConsentIsChecked(!consentIsChecked);
                                                if (!consentIsChecked) {
                                                    setNotApplicableChecked(false);
                                                    setUkRelayChecked(false);
                                                    setTranslatorChecked(false);
                                                    setAdditionalNeeds(false);
                                                    setMentalHealthChecked(false);
                                                    setPhysicalNeedsChecked(false);
                                                    setSaveButtonEnabled(false);
                                                }
                                            }}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                        />
                                    }
                                    label="By selecting this box, you are giving us consent to do this."
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, mt: 3, fontWeight: 'bold' }}>
                                    When we communicate with you, we want to be able to do this in a way that best fits your circumstances and needs. Are any of the following statements relevant to you and your personal circumstances? Please select from the below:
                                </Typography>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={notApplicableChecked}
                                            onChange={() => setNotApplicableChecked(!notApplicableChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!middleCheckboxesEnabled || ukRelayChecked || translatorChecked}
                                        />
                                    }
                                    label="Not applicable"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={ukRelayChecked}
                                            onChange={() => setUkRelayChecked(!ukRelayChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!middleCheckboxesEnabled || notApplicableChecked}
                                        />
                                    }
                                    label="I use the UK Relay Service when communicating on the phone"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={translatorChecked}
                                            onChange={() => setTranslatorChecked(!translatorChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!middleCheckboxesEnabled || notApplicableChecked}
                                        />
                                    }
                                    label="I usually speak through a translator or appointed 3rd party so I will have someone with me if I contact you"
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />
                            </Box>

                            <Box>
                                <Typography sx={{ mb: 1, mt: 3, fontWeight: 'bold' }}>Additional Support</Typography>

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={additionalNeeds}
                                            onChange={() => setAdditionalNeeds(!additionalNeeds)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!callbackCheckboxesEnabled || mentalHealthChecked || physicalNeedsChecked}
                                        />
                                    }
                                    label={<>I have <strong>additional accessibility needs</strong> that don't appear on the list above and would require a callback to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={mentalHealthChecked}
                                            onChange={() => setMentalHealthChecked(!mentalHealthChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!callbackCheckboxesEnabled || additionalNeeds || physicalNeedsChecked}
                                        />
                                    }
                                    label={<>I have <strong>mental health needs </strong> that are impacting me financially and would like to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={physicalNeedsChecked}
                                            onChange={() => setPhysicalNeedsChecked(!physicalNeedsChecked)}
                                            inputProps={{ "aria-label": "controlled" }}
                                            sx={{
                                                marginRight: { xs: "8px", sm: 0 },
                                                marginBottom: { xs: "8px", sm: 0 },
                                            }}
                                            disabled={!callbackCheckboxesEnabled || additionalNeeds || mentalHealthChecked}
                                        />
                                    }
                                    label={<>I have <strong>physical health needs </strong> that are impacting me financially and would like a callback to discuss them.</>}
                                    sx={{ display: "flex", alignItems: "center", margin: 0 }}
                                />

                                <Typography type="body1" sx={{ fontStyle: "italic", mb: 1, mt: 1, fontSize: "14px" }}>
                                    Once you click Save my preferences with one of the additional support options, you will be redirected to schedule your callback request
                                </Typography>
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    flex: 1,
                                    mt: 3,
                                    borderRadius: "0",
                                    width: { xs: "15em", lg: "20em" },
                                    backgroundColor: "primary.dark",
                                    color: "#fff",
                                    "&:hover": {
                                        backgroundColor: "primary.light",
                                        color: "primary.dark",
                                    },
                                }}
                                disabled={!saveButtonEnabled}
                                onClick={handleSave}
                            >
                                Save my preferences
                            </Button>
                        </>
                    ) : (
                        <Box sx={{ textAlign: "center", mt: 8 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
                            <Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: 'bold' }}>
                                Thank you for submitting your accessibility preferences!
                            </Typography>
                            <Typography sx={{ mt: 2, mb: 2 }}>
                                We've received your details and will use them to make sure you get the support you need. If your circumstances change, you can update your preferences at any time through your account.

                                {!clientConfig?.layout.callMe.active &&
                                    middleCheckboxesEnabled &&
                                    (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) &&
                                    (
                                        <Typography sx={{ mt: 2 }}>
                                            * Unfortunately our call back service is currently unavailable. Please contact us on
                                            <Typography sx={{ ml: 1 }}>
                                                <a href="tel:01737 237 370" style={{ textDecoration: 'underline' }}>
                                                    01737 237 370
                                                </a>
                                            </Typography>
                                        </Typography>
                                    )}

                            </Typography>
                            {!clientConfig?.layout.callMe.active &&
                                middleCheckboxesEnabled &&
                                (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && <Button
                                    onClick={handleReturnHome}
                                    sx={{
                                        flex: 1,
                                        mt: 3,
                                        borderRadius: "0",
                                        backgroundColor: "primary.dark",
                                        width: { xs: "15em", lg: "20em" },
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: "primary.light",
                                            color: "primary.dark",
                                        },
                                    }}>Return Home</Button>}
                            {clientConfig?.layout.callMe.active &&
                                middleCheckboxesEnabled &&
                                !(additionalNeeds || mentalHealthChecked || physicalNeedsChecked) && <Button
                                    onClick={handleReturnHome}
                                    sx={{
                                        flex: 1,
                                        mt: 3,
                                        borderRadius: "0",
                                        backgroundColor: "primary.dark",
                                        width: { xs: "15em", lg: "20em" },
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: "primary.light",
                                            color: "primary.dark",
                                        },
                                    }}>Return Home</Button>}

                            {clientConfig?.layout.callMe.active && (additionalNeeds || mentalHealthChecked || physicalNeedsChecked) &&
                                <Button
                                    onClick={handleOpenCallback}
                                    sx={{
                                        flex: 1,
                                        mt: 3,
                                        borderRadius: "0",
                                        backgroundColor: "primary.dark",
                                        width: { xs: "15em", lg: "20em" },
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: "primary.light",
                                            color: "primary.dark",
                                        },
                                    }}>Set a call back</Button>
                            }
                        </Box>
                    )}
                    {accessToken && clientConfig?.layout.callMe.active && (
                        <CallMeDialog open={openCallback} onClose={handleCloseCallback} dateOfBirth={formState?.dateOfBirth} />
                    )}
                </Container>

                {setShowFooter(true)}
            </>
        )
    );
};

export default Accessibility;
